import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAxios } from '../context/AxiosContext';

const Landing = () => {
  const { isAuthenticated } = useAxios();

  const features = [
    {
      emoji: '⚡',
      title: 'Lightning Fast Integration',
      description: 'Embed Discord chat directly on your website with just a simple iframe. No complex setup required.',
    },
    {
      emoji: '🛡️',
      title: 'Enterprise Security',
      description: 'Your Discord server data is protected with top-tier security measures and encryption.',
    },
    {
      emoji: '🚀',
      title: 'Real-Time Performance',
      description: 'Optimized for speed with instant message updates and WebSocket connections.',
    },
    {
      emoji: '💻',
      title: 'Developer Friendly',
      description: 'Simple API with comprehensive documentation to get you started in minutes.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ pt: 6, pb: 4, textAlign: 'center' }}>
          <Typography
            component="h1"
            variant="h1"
            gutterBottom
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #10b981 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              animation: 'fadeInUp 1s ease-out',
              '@keyframes fadeInUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(30px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            ✨ Wizcord
          </Typography>
          <Typography
            component="h2"
            variant="h4"
            color="text.secondary"
            sx={{ 
              mb: 4, 
              maxWidth: '700px', 
              mx: 'auto',
              fontWeight: 400,
              lineHeight: 1.5,
              animation: 'fadeInUp 1s ease-out 0.2s both',
            }}
          >
            The magical way to embed Discord chat on your website. 
            Connect your community directly to your site with our seamless iframe integration.
          </Typography>

          {/* Live Demo Section - Moved up here */}
          <Box sx={{ 
            mb: 6,
            animation: 'fadeInUp 1s ease-out 0.4s both',
          }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mx: 'auto',
                maxWidth: '1000px',
                bgcolor: 'background.paper',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                  borderColor: 'rgba(99, 102, 241, 0.3)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '600px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '2px solid rgba(99, 102, 241, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'rgba(99, 102, 241, 0.5)',
                    boxShadow: '0 0 30px rgba(99, 102, 241, 0.2)',
                  },
                }}
              >
                <iframe
                  src="https://wizcord.io/iframe?channelId=1396512927846240367"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="Discord Chat Demo"
                  allow="clipboard-write"
                />
              </Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 2, 
                  textAlign: 'center',
                  fontSize: '0.95rem',
                }}
              >
                💬 Live Discord channel embedded directly in this webpage
              </Typography>
            </Paper>
          </Box>
          
          {!isAuthenticated ? (
            <Box sx={{ 
              mb: 6,
              animation: 'fadeInUp 1s ease-out 0.6s both',
            }}>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="large"
                sx={{ 
                  mr: 3, 
                  mb: 2,
                  fontSize: '1.1rem',
                  px: 4,
                  py: 1.5,
                }}
              >
                Get Started Free
              </Button>
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                size="large"
                sx={{ 
                  mb: 2,
                  fontSize: '1.1rem',
                  px: 4,
                  py: 1.5,
                }}
              >
                Sign In
              </Button>
            </Box>
          ) : (
            <Box sx={{ 
              mb: 6,
              animation: 'fadeInUp 1s ease-out 0.6s both',
            }}>
              <Button
                component={Link}
                to="/dashboard"
                variant="contained"
                size="large"
                sx={{ 
                  mr: 2, 
                  mb: 2,
                  fontSize: '1.1rem',
                  px: 4,
                  py: 1.5,
                }}
              >
                Go to Dashboard
              </Button>
            </Box>
          )}
        </Box>

        {/* Features Section */}
        <Box sx={{ 
          mb: 12,
          animation: 'fadeInUp 1s ease-out 0.8s both',
        }}>
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ 
              mb: 8,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Why Choose Wizcord?
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    textAlign: 'center', 
                    p: 3,
                    background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
                    backdropFilter: 'blur(10px)',
                    animation: `fadeInUp 1s ease-out ${0.2 * index + 1}s both`,
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        mb: 3,
                        fontSize: '3rem',
                        filter: 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.3))',
                      }}
                    >
                      {feature.emoji}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* How It Works Section */}
        <Box sx={{ 
          mb: 12,
          animation: 'fadeInUp 1s ease-out 1.2s both',
        }}>
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ 
              mb: 8,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            How It Works
          </Typography>
          <Grid container spacing={6}>
            {[
              {
                step: '01',
                title: 'Create Account',
                description: 'Sign up for your free Wizcord account and verify your email to get started.',
              },
              {
                step: '02',
                title: 'Add Server',
                description: 'Register your Discord server ID in the dashboard to enable iframe embedding.',
              },
              {
                step: '03',
                title: 'Embed & Launch',
                description: 'Copy the iframe code and paste it into your website. Your chat is live instantly!',
              },
            ].map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      boxShadow: '0 10px 30px rgba(99, 102, 241, 0.3)',
                      animation: `pulse 2s ease-in-out infinite ${index * 0.5}s`,
                      '@keyframes pulse': {
                        '0%, 100%': {
                          transform: 'scale(1)',
                        },
                        '50%': {
                          transform: 'scale(1.05)',
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'white',
                        fontWeight: 800,
                      }}
                    >
                      {step.step}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {step.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 8, opacity: 0.1 }} />

        {/* CTA Section */}
        <Box sx={{ 
          textAlign: 'center', 
          pb: 12,
          animation: 'fadeInUp 1s ease-out 1.4s both',
        }}>
          <Typography 
            variant="h3" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              mb: 3,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Ready to Get Started?
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              mb: 6,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Join thousands of websites already using Wizcord to embed Discord chat and enhance their community experience.
          </Typography>
          {!isAuthenticated && (
            <Button
              component={Link}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                fontSize: '1.2rem',
                px: 6,
                py: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 15px 35px rgba(99, 102, 241, 0.4)',
                },
              }}
            >
              🚀 Start Free with Wizcord
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Landing; 